import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiMapPin, FiCheck, FiArrowRight } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { destinations, SITE_URL, AIRPORT_COORDS, PORT_COORDS } from '../config/seo-data';

const buildBookingUrl = (pickup, dropoff) => {
  const params = new URLSearchParams();
  if (pickup) {
    params.set('pickup_name', pickup.name);
    params.set('pickup_lat', pickup.lat);
    params.set('pickup_lng', pickup.lng);
  }
  if (dropoff) {
    params.set('dropoff_name', dropoff.name);
    params.set('dropoff_lat', dropoff.lat);
    params.set('dropoff_lng', dropoff.lng);
  }
  return `/booking?${params.toString()}`;
};

const ease = [0.22, 1, 0.36, 1];

const northAreas = ['North Corfu', 'Northeast Corfu', 'Northwest Corfu'];

const northDestinations = destinations
  .filter((d) => northAreas.includes(d.area))
  .sort((a, b) => (a.popular === b.popular ? 0 : a.popular ? -1 : 1))
  .map((d) => ({
    ...d,
    link: `/transfers/${d.slug}`,
    bookingLink: d.coords
      ? buildBookingUrl(AIRPORT_COORDS, { name: d.name, ...d.coords })
      : '/booking'
  }));

const highlights = [
  { title: 'Kassiopi', desc: 'Byzantine castle ruins, crystal-clear harbour swimming, and charming waterfront tavernas facing Albania.', time: '50-65 min' },
  { title: 'Sidari', desc: 'The legendary Canal d\'Amour rock formations, vibrant nightlife, and boats to the Diapontia Islands.', time: '45-55 min' },
  { title: 'Acharavi', desc: 'Kilometres of sandy beach, traditional village life, and the Hydropolis water park for families.', time: '45-55 min' },
  { title: 'Roda', desc: 'Ancient temple ruins, relaxed atmosphere, and warm shallow waters perfect for children.', time: '45-55 min' },
  { title: 'Barbati', desc: 'Blue Flag pebble beach beneath Mount Pantokrator, one of Corfu\'s most stunning coastal settings.', time: '25-35 min' },
  { title: 'Nissaki', desc: 'Hidden coves with turquoise water, exceptional snorkelling, and total peace and quiet.', time: '30-40 min' },
];

const NorthCorfuTransfers = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | North Corfu Airport Transfers"
    description="Book a private transfer from Corfu Airport to North Corfu destinations, Kassiopi, Sidari, Acharavi, Roda, Barbati, Nissaki & more. Fixed prices, Mercedes-Benz, meet & greet. 24/7 service."
    keywords="north corfu transfer, north corfu airport transfer, kassiopi transfer, sidari transfer, acharavi transfer, roda transfer, barbati transfer, corfu north coast transfer, northeast corfu transfer, northwest corfu transfer, corfu airport to kassiopi, corfu airport to sidari"
    canonical={`${SITE_URL}/north-corfu-transfers`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/kassiopi-harbor-vclass.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Kassiopi · Sidari · Acharavi · Roda · Barbati · Nissaki
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          North Corfu
          <br />
          <span className="font-light italic">Transfers</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-gray-300 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Private airport transfers to every destination on Corfu's beautiful north coast. Fixed prices, Mercedes-Benz comfort, door-to-door service.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to={`/booking?pickup_name=${encodeURIComponent(AIRPORT_COORDS.name)}&pickup_lat=${AIRPORT_COORDS.lat}&pickup_lng=${AIRPORT_COORDS.lng}`}
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Book North Corfu Transfer
          </Link>
          <a
            href="#destinations"
            className="text-white/80 hover:text-white px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 text-sm tracking-wide"
          >
            View All Destinations
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-[1px] h-12 bg-gradient-to-b from-white/50 to-transparent"
          />
        </motion.div>
      </div>
    </section>

    {/* Why North Corfu */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">The North Coast</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Visitors Choose North Corfu
          </h2>
          <p className="text-[#5b4d4f] text-lg max-w-3xl mx-auto font-light leading-relaxed">
            North Corfu stretches from the dramatic northeast coast, where Kassiopi's Byzantine castle overlooks Albania, around to the sandy beaches of Sidari and the peaceful northwest shores of Arillas. It's where Corfu feels most authentically Greek: harbour villages, hidden coves, and mountain roads through ancient olive groves.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
            className="bg-[#faf7f6] border border-black/10 rounded-2xl p-8"
          >
            <h3 className="text-[#1a1012] text-xl font-medium mb-4">Getting to North Corfu</h3>
            <p className="text-[#5b4d4f] text-sm leading-relaxed font-light mb-4">
              North Corfu destinations are 25-50 km from the airport, with journey times of 30-65 minutes depending on your exact location. The coastal road passes through some of Corfu's most scenic landscapes.
            </p>
            <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">
              Public buses run infrequently to North Corfu (2-3 times daily), making a pre-booked private transfer the most reliable option, especially if arriving on a late flight or with children and luggage.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1, ease }}
            className="bg-[#faf7f6] border border-black/10 rounded-2xl p-8"
          >
            <h3 className="text-[#1a1012] text-xl font-medium mb-4">What's Included</h3>
            <ul className="space-y-3">
              {[
                'Meet & greet at Corfu Airport arrivals',
                'Real-time flight monitoring, we adjust for delays',
                'Door-to-door service to your hotel, villa, or resort',
                'Comfortable Mercedes-Benz with AC & WiFi',
                'Child & booster seats on request (free)',
                'Fixed prices, no meters, no surprises',
                'Free cancellation up to 24 hours before',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#5b4d4f] text-sm font-light">
                  <FiCheck className="w-4 h-4 text-[#ba1609] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Highlighted Destinations */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Popular Destinations</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            North Corfu Highlights
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08, ease }}
              className="group relative bg-white border border-black/10 rounded-2xl p-8 shadow-sm hover:shadow-lg hover:border-[#ba1609]/30 transition-[color,background-color,border-color,box-shadow] duration-500"
            >
              <div className="mb-4">
                <h3 className="text-[#1a1012] text-xl font-medium group-hover:text-[#ba1609] transition-colors">{h.title}</h3>
              </div>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light mb-4">{h.desc}</p>
              <div className="flex items-center gap-3 text-[#5b4d4f] text-xs mb-6">
                <span className="flex items-center gap-1"><FiClock className="w-3 h-3" /> {h.time}</span>
                <span className="flex items-center gap-1"><FiMapPin className="w-3 h-3" /> From Airport</span>
              </div>
              <Link
                to={`/transfers/${h.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="inline-flex items-center gap-2 text-[#ba1609] text-sm group-hover:text-[#8e1107] transition-colors"
              >
                View details <FiArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* All North Destinations Table */}
    <section id="destinations" className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-4"
        >
          <div>
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">All Routes</p>
            <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
              North Corfu Transfer Routes
            </h2>
          </div>
          <p className="text-[#5b4d4f] text-sm max-w-md font-light">
            Per vehicle, not per person. Every transfer includes meet & greet, flight monitoring, and luggage assistance.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {northDestinations.map((r, i) => (
            <motion.div
              key={r.slug}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
            >
              <div className="group bg-[#faf7f6] border border-black/10 rounded-xl px-5 py-4 shadow-sm hover:shadow-md hover:border-[#ba1609]/30 transition-all duration-300">
                <div className="flex items-center justify-between">
                  <Link to={r.link} className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[#1a1012] font-medium group-hover:text-[#ba1609] transition-colors">
                        {r.name}
                      </h3>
                      {r.popular && (
                        <span className="text-[9px] bg-[#fbeceb] text-[#ba1609] px-2 py-0.5 rounded-full uppercase tracking-wider border border-[#ba1609]/20">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[#5b4d4f] text-xs">
                      <span>{r.distance}</span>
                      <span className="w-1 h-1 rounded-full bg-black/20" />
                      <span>{r.duration}</span>
                    </div>
                  </Link>
                  <Link
                    to={r.bookingLink}
                    className="text-[11px] bg-[#ba1609] hover:bg-[#8e1107] text-white px-3 py-1.5 rounded-full transition-all duration-300 shrink-0 ml-3"
                  >
                    Book
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 lg:py-32 bg-[#ba1609] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/15" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
        >
          <p className="text-white/85 text-xs uppercase tracking-[0.4em] mb-4">Ready to Book?</p>
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book Your North Corfu Transfer
          </h2>
          <p className="text-white/85 text-lg max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Mercedes-Benz comfort. Fixed prices confirmed at booking. Your driver waiting at arrivals with a name sign.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={`/booking?pickup_name=${encodeURIComponent(AIRPORT_COORDS.name)}&pickup_lat=${AIRPORT_COORDS.lat}&pickup_lng=${AIRPORT_COORDS.lng}`}
              className="bg-white text-[#ba1609] px-10 py-4 rounded-full font-medium hover:bg-gray-100 transition-all duration-300 text-base tracking-wide"
            >
              Book Transfer Now
            </Link>
            <Link
              to="/airport-transfers"
              className="text-white/80 hover:text-white px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 text-sm tracking-wide"
            >
              View All Airport Transfers
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  </SEOPageLayout>
);

export default NorthCorfuTransfers;
